// Dependencies
import React from 'react';

// External Components
import Head from 'next/head';

// Material UI
import darkScrollbar from '@mui/material/darkScrollbar';
import CssBaseline from '@mui/material/CssBaseline';
import GlobalStyles from '@mui/material/GlobalStyles';
import * as locales from '@mui/material/locale';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  createTheme,
  ThemeProvider as MUIThemeProvider,
  alpha,
  responsiveFontSizes,
  StyledEngineProvider,
} from '@mui/material/styles';

// Utils
import palette from './palette';
import typography from './typography';
import breakpoints from './breakpoints';
import shadows, { customShadows } from './shadows';
import getColorPresets from 'src/utils/getColorPresets';
import ComponentsOverrides from './overrides';
import { useSelector } from 'src/store/store';
import { useRouter } from 'next/router';
import { allLangs } from 'src/config';
// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
};

const locale = {
  ru: locales.ruRU,
  en: locales.enUS,
  ar: locales.arSA,
};

export default function ThemeProvider({ children }: Props) {
  // Language
  const { locale: language } = useRouter();

  // Get settings context
  const stateTheme = useSelector((state) => state.theme.mode);
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const themeMode = React.useMemo(() => {
    if (stateTheme === 'auto') {
      return prefersDarkMode ? 'dark' : 'light';
    }
    return stateTheme;
  }, [stateTheme, prefersDarkMode]);
  const currentLanguage = allLangs.find((x) => x.value === language);

  // Crate main theme
  const coloredTheme = React.useMemo(() => {
    const isLight = stateTheme === 'auto' ? themeMode === 'light' : stateTheme === 'light';
    // const color = isLight ? 'blue' : 'cyan';
    const color = 'default';
    return createTheme({
      palette: {
        ...palette[themeMode as keyof typeof palette],
        primary: getColorPresets(color),
      },
      typography: {
        ...typography,
        fontFamily: currentLanguage?.fontFamily || typography.fontFamily,
      },
      breakpoints,
      shape: { borderRadius: 8 },
      direction: ['ar', 'he'].includes(language || 'en') ? 'rtl' : 'ltr',
      shadows: isLight ? shadows.light : shadows.dark,
      customShadows: {
        ...(isLight ? customShadows.light : customShadows.dark),
        primary: `0 8px 16px 0 ${alpha(getColorPresets(color).main, 0.24)}`,
      },
    });
  }, [language, stateTheme, themeMode, currentLanguage]);

  // Overrides Theme
  const overridesTheme = React.useMemo(
    () =>
      createTheme(
        {
          ...coloredTheme,
          components: {
            ...coloredTheme.components,
            ...ComponentsOverrides(coloredTheme),
            MuiCssBaseline: {
              ...ComponentsOverrides(coloredTheme).MuiCssBaseline,
              ...(coloredTheme?.components?.MuiCssBaseline || {}),
              body:
                coloredTheme.palette.mode === 'dark'
                  ? {
                      ...(ComponentsOverrides(coloredTheme).MuiCssBaseline?.body || {}),
                      ...darkScrollbar(),
                      height: '100%',
                    }
                  : {
                      ...(ComponentsOverrides(coloredTheme).MuiCssBaseline?.body || {}),
                      height: '100%',
                    },
              styleOverrides: (theme) => [
                ComponentsOverrides(coloredTheme).MuiCssBaseline.styleOverrides,
              ],
            },
          },
        },
        locale[language as keyof typeof locale] || locale.en
      ),
    [coloredTheme, language]
  );

  // Add Responsive font size to theme
  const responsivelyTheme = responsiveFontSizes(overridesTheme);

  return (
    <>
      <Head>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        {currentLanguage && <link href={currentLanguage.fontPath} rel="stylesheet" />}
      </Head>
      <StyledEngineProvider injectFirst>
        <MUIThemeProvider theme={responsivelyTheme}>
          <CssBaseline />
          <GlobalStyles
            styles={{
              a: {
                color: 'inherit',
                textDecoration: 'none',
              },
              body: {
                ...(coloredTheme.palette.mode === 'dark'
                  ? {
                      ...(ComponentsOverrides(coloredTheme).MuiCssBaseline?.body || {}),
                      ...darkScrollbar(),
                      height: '100%',
                    }
                  : {
                      ...(ComponentsOverrides(coloredTheme).MuiCssBaseline?.body || {}),
                      height: '100%',
                    }),
              },
            }}
          />
          <Head>
            <meta name="theme-color" content={responsivelyTheme.palette.background.default} />
          </Head>
          {children}
        </MUIThemeProvider>
      </StyledEngineProvider>
    </>
  );
}
